import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../Css/demoSignin.css";
import { setProfile, storeToken } from "../../services/localStorageServices";

const DEMO_PROFILE_KEY = "demoProfile";
const DEMO_LOGIN_TOKEN = "demoLoginToken";

const DemoSignin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);

  // Check if user is already logged in
  useEffect(() => {
    const loginToken = localStorage.getItem(DEMO_LOGIN_TOKEN);
    if (loginToken) {
      navigate("/");
    }
  }, [navigate]);

  const setFeedback = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleSignIn = (event) => {
    event.preventDefault();
    setLoading(true);
    const email = formData.email.trim().toLowerCase();
    const password = formData.password;

    if (!email || !password) {
      setFeedback("Please enter both email and password.", "error");
      setLoading(false);
      return;
    }

    const savedProfile = JSON.parse(localStorage.getItem(DEMO_PROFILE_KEY) || "null");
    if (savedProfile && email === savedProfile.email && password === savedProfile.password) {
      // Generate token similar to API response
      const token = Math.random().toString(36).substring(2, 35) + Math.random().toString(36).substring(2, 35);
      
      // Create profile object with only email and password for login (others as null)
      const profileData = {
        user_email: email,
        user_password: password,
        user_name: null,
        user_phone: null,
      };
      
      // Store token and profile using the provided functions
      storeToken(token);
      setProfile(profileData);
      
      setFeedback(`Welcome back, ${savedProfile?.name || email}!`, "success");
      resetForm();
      setTimeout(() => navigate("/"), 500);
    } else {
      setFeedback("Email or password did not match. Please register first or try again.", "error");
    }
    setLoading(false);
  };

  const handleSignUp = (event) => {
    event.preventDefault();
    setLoading(true);

    if (!formData.name.trim()) {
      setFeedback("Please enter your full name.", "error");
      setLoading(false);
      return;
    }

    if (!formData.email.trim() || !formData.phone.trim() || !formData.password) {
      setFeedback("Please fill in all required fields.", "error");
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFeedback("Passwords do not match.", "error");
      setLoading(false);
      return;
    }

    const newUser = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      password: formData.password,
    };

    localStorage.setItem(DEMO_PROFILE_KEY, JSON.stringify(newUser));
    
    // Generate token similar to API response
    const token = Math.random().toString(36).substring(2, 35) + Math.random().toString(36).substring(2, 35);
    
    // Create profile object with name, email, phone, and password for registration
    const profileData = {
      user_name: newUser.name,
      user_email: newUser.email,
      user_phone: newUser.phone,
      user_password: newUser.password,
    };
    
    // Store token and profile using the provided functions
    storeToken(token);
    setProfile(profileData);
    
    setFeedback("Registration completed. Welcome aboard!", "success");
    resetForm();
    setTimeout(() => navigate("/"), 500);
    setLoading(false);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
      background: "linear-gradient(135deg, #0f172a 0%, #020617 45%, #0d1b2a 100%)",
      color: "#f8fafc",
    },
    card: {
      width: "100%",
      maxWidth: "480px",
      borderRadius: "28px",
      overflow: "hidden",
      boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
      background: "rgba(14, 23, 36, 0.96)",
      border: "1px solid rgba(255,255,255,0.08)",
    },
    formPanel: {
      width: "100%",
      padding: "40px 40px 36px",
      position: "relative",
    },
    tabBar: {
      display: "flex",
      gap: "10px",
      marginBottom: "28px",
    },
    tabButton: {
      flex: 1,
      border: "none",
      borderRadius: "999px",
      padding: "14px 18px",
      fontSize: "0.95rem",
      fontWeight: 700,
      cursor: "pointer",
      transition: "background 0.25s ease, color 0.25s ease",
      background: "rgba(255,255,255,0.04)",
      color: "rgba(248,250,252,0.84)",
    },
    tabButtonActive: {
      background: "linear-gradient(135deg, #38bdf8, #7c3aed)",
      color: "white",
    },
    form: {
      display: "grid",
      gap: "16px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      color: "rgba(248,250,252,0.8)",
      fontSize: "0.95rem",
      fontWeight: 600,
    },
    input: {
      width: "100%",
      minHeight: "48px",
      padding: "14px 16px",
      borderRadius: "14px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.04)",
      color: "white",
      outline: "none",
      fontSize: "0.95rem",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    },
    inputFocus: {
      borderColor: "rgba(56, 189, 248, 0.9)",
      boxShadow: "0 0 0 4px rgba(56, 189, 248, 0.12)",
    },
    button: {
      width: "100%",
      border: "none",
      borderRadius: "14px",
      padding: "14px 0",
      fontSize: "1rem",
      fontWeight: 700,
      cursor: "pointer",
      color: "#0f172a",
      background: "#f8fafc",
      transition: "transform 0.2s ease",
    },
    buttonLoading: {
      opacity: 0.9,
      cursor: "default",
    },
    message: {
      padding: "14px 18px",
      borderRadius: "14px",
      border: messageType === "success" ? "1px solid rgba(52,211,153,0.25)" : "1px solid rgba(248,113,113,0.25)",
      background: messageType === "success" ? "rgba(16,185,129,0.12)" : "rgba(248,113,113,0.1)",
      color: messageType === "success" ? "#a7f3d0" : "#fecaca",
      marginBottom: "16px",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.formPanel}>
          <div style={styles.tabBar}>
            <button
              type="button"
              onClick={() => {
                setMode("signin");
                setMessage("");
              }}
              style={{
                ...styles.tabButton,
                ...(mode === "signin" ? styles.tabButtonActive : {}),
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("signup");
                setMessage("");
              }}
              style={{
                ...styles.tabButton,
                ...(mode === "signup" ? styles.tabButtonActive : {}),
              }}
            >
              Register
            </button>
          </div>

          {message ? <div style={styles.message}>{message}</div> : null}

          <form onSubmit={mode === "signin" ? handleSignIn : handleSignUp} style={styles.form}>
            {mode === "signup" && (
              <label style={styles.label}>
                Full Name
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  style={styles.input}
                />
              </label>
            )}

            <label style={styles.label}>
              Email address
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                style={styles.input}
              />
            </label>

            {mode === "signup" && (
              <label style={styles.label}>
                Phone number
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9999999999"
                  style={styles.input}
                />
              </label>
            )}

            <label style={styles.label}>
              Password
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password"
                style={styles.input}
              />
            </label>

            {mode === "signup" && (
              <label style={styles.label}>
                Confirm Password
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repeat password"
                  style={styles.input}
                />
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...(loading ? styles.buttonLoading : {}),
              }}
            >
              {loading ? "Processing..." : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemoSignin;